import React, { useEffect, useState, type FC } from 'react';
import { Form, Tag, Typography } from 'antd';

import { InputText, Link, Modal, Radio, Select, Space, Textarea } from '@g2u/ui-kit';
import { Toast, useEmailTemplates, useSendEmail, useSendSms, useSmsTemplates, validateFormSilently } from 'helpers';
import { LinkType } from 'types';

import styles from './create-message-modal.module.scss';

export namespace CreateMessageModal {
  type To = {
    destination: string;
    name?: string;
  };

  type EntityType = 'task' | 'job';

  enum MessageType {
    sms = 'sms',
    email = 'email',
  }

  type Props = {
    isOpen: boolean;
    onClose: () => void;
    subtitle: string;
    customerTo?: To;
    technicianTo?: To;
    messageType: keyof typeof MessageType;
    id: number;
    entityType: EntityType;
    hasNotification?: boolean;
  };

  type Fields = {
    sendTo: LinkType;
    to: string;
    cc?: string[];
    bcc?: string[];
    subject: string;
    template: string;
    message: string;
  };

  const DefaultFieldWidth = 350;
  const WideFieldWidth = 400;

  // https://emailregex.com/
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const mobileRegex = /^04\d{2}(\s?\d{3}){2}$/;

  export const $: FC<Props> = ({
    isOpen,
    onClose,
    subtitle,
    customerTo,
    technicianTo,
    id,
    messageType,
    entityType,
    hasNotification,
  }) => {
    const [form] = Form.useForm<Fields>();
    const [isSubmittable, setIsSubmittable] = useState(false);

    const values = Form.useWatch([], form);

    useEffect(() => {
      form.isFieldsTouched() && validateFormSilently(form, setIsSubmittable);
    }, [form, values]);

    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);

    const { templates: emailTemplates } = useEmailTemplates(entityType === 'task' ? id : undefined); // TODO: job filter is not supported by BE
    const { templates: smsTemplates } = useSmsTemplates();

    const isEmailMessage = messageType === MessageType.email;
    const templates = isEmailMessage ? emailTemplates : smsTemplates;

    const { sendEmail, isSending: isEmailSending } = useSendEmail(onClose);
    const { sendTaskSms, sendJobSms, isSending: isSmsSending, isSmsSent } = useSendSms(onClose);

    useEffect(() => {
      hasNotification &&
        isSmsSent &&
        Toast.show({
          description: 'SMS has been sent',
          type: Toast.Type.Success,
        });
    }, [isSmsSent]);

    const isSending = isEmailSending || isSmsSending;

    const commonCcBccProps: Partial<Select.Props> = {
      mode: 'tags',
      placeholder: 'Separate multiple email addresses with commas',
      notFoundContent: null,
      showSearch: false,
      tokenSeparators: [','],
      suffixIcon: null,
      width: DefaultFieldWidth,
      rules: [
        {
          type: 'array',
          defaultField: {
            type: 'email',
          },
          message: '${label} has an invalid email',
        },
      ],
      normalize: (v: string[]) => [...new Set(v.map((s) => s.trim()))],
      rootClassName: styles.createMessageModal__multiEmailSelect,
      tagRender: ({ value, onClose }) => (
        <Tag
          closable
          onClose={onClose}
          color={emailRegex.test(value) ? 'default' : 'error'}
          rootClassName={styles.createMessageModal__multiEmailSelect_emailTag}
        >
          <Typography.Text ellipsis rootClassName={styles.createMessageModal__multiEmailSelect_emailTag_label}>
            {value}
          </Typography.Text>
        </Tag>
      ),
    };

    const toMap = {
      [LinkType.Customer]: customerTo!,
      [LinkType.Technician]: technicianTo!,
      [LinkType.Other]: {
        destination: values?.to,
        name: undefined,
      },
    } satisfies Record<Fields['sendTo'], To>;

    const finishHandler = (values: Fields) => {
      const to = toMap[values.sendTo];

      if (entityType === 'task') {
        if (isEmailMessage) {
          sendEmail({ ...values, taskId: id, email: to.destination, name: to.name });
        } else {
          sendTaskSms({ taskId: id, message: values.message, to_number: to.destination, sms_to: values.sendTo });
        }
      }

      if (entityType === 'job') {
        if (isEmailMessage) {
          // send email here if needed
        } else {
          sendJobSms({ id, message: values.message, to_number: to.destination, sms_to: values.sendTo });
        }
      }
    };

    const title = [`New ${isEmailMessage ? 'Email' : 'SMS'}`, subtitle || ''].join(' | ');

    return (
      <Modal.$
        dataTestId="create-message-modal"
        open={isOpen}
        title={title}
        okText="Send"
        okButtonProps={{ disabled: !isSubmittable }}
        onCancel={onClose}
        onClose={onClose}
        onOk={form.submit}
        confirmLoading={isSending}
        cancelButtonProps={{ disabled: isSending }}
      >
        <Form
          form={form}
          onFinish={finishHandler}
          labelCol={{ style: { width: 130 } }}
          labelAlign="left"
          layout="horizontal"
          disabled={isSending}
          preserve={false}
        >
          <Radio.$
            name="sendTo"
            label="Send to"
            options={[
              {
                label: 'Customer',
                value: LinkType.Customer,
                disabled: !customerTo?.destination,
              },
              {
                label: 'Technician',
                value: LinkType.Technician,
                disabled: !technicianTo?.destination,
              },
              {
                label: 'Other',
                value: LinkType.Other,
              },
            ]}
            onChange={({ target }) => form.setFieldValue('to', toMap[target.value as LinkType].destination)}
            rules={[{ required: true }]}
          />
          <Space.$ className="ant-form-item">
            <InputText.$
              name="to"
              label={isEmailMessage ? 'To Email' : 'To number'}
              readonly={values?.sendTo !== LinkType.Other}
              width={DefaultFieldWidth}
              rules={[
                { required: true },
                isEmailMessage
                  ? { type: 'email', message: 'Email entered is invalid' }
                  : { pattern: mobileRegex, message: 'Mobile number entered is invalid' },
              ]}
            />
            {isEmailMessage && (
              <>
                <Link.$ disabled={showCc} onClick={() => setShowCc(true)}>
                  CC
                </Link.$>
                <Link.$ disabled={showBcc} onClick={() => setShowBcc(true)}>
                  BCC
                </Link.$>
              </>
            )}
          </Space.$>
          {showCc ? <Select.$ name="cc" label="CC" {...commonCcBccProps} /> : null}
          {showBcc ? <Select.$ name="bcc" label="BCC" {...commonCcBccProps} /> : null}
          {isEmailMessage && (
            <InputText.$ name="subject" label="Subject" width={DefaultFieldWidth} rules={[{ required: true }]} />
          )}
          <Select.$
            name="template"
            label="Template"
            width={WideFieldWidth}
            options={templates?.map(({ ID, name, ...rest }) => ({
              value: ID,
              label: name,
              message: 'message' in rest ? rest.message : rest.body,
              subject: 'subject' in rest ? rest.subject : undefined,
            }))}
            onChange={(_, option: any) => form.setFieldsValue({ message: option?.message, subject: option?.subject })}
          />
          <Textarea.$ name="message" label="Message" rules={[{ required: true }]} />
        </Form>
      </Modal.$>
    );
  };
}
